// src/web/routes/_authenticated/home.tsx
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'
import { BrowseView } from '@/components/features/BrowseView'
import { DownloadModal } from '@/components/features/DownloadModal'
import { RecentActivity } from '@/components/features/RecentActivity'
import { StatsMeter } from '@/components/features/StatsMeter'
import type { Asset } from '@/lib/hooks/useAssets'
import { useFilterStore } from '@/stores/filters'

// Define search params schema
const homeSearchSchema = z.object({
	folder: z.string().optional(),
})

export const Route = createFileRoute('/_authenticated/home')({
	validateSearch: homeSearchSchema,
	component: HomePage,
})

function HomePage() {
	const navigate = useNavigate()
	const search = useSearch({ from: '/_authenticated/home' })
	const { selectedFolder, setSelectedFolder } = useFilterStore()

	// Download modal state
	const [downloadModalOpen, setDownloadModalOpen] = useState(false)
	const [downloadFile, setDownloadFile] = useState<{
		id: string
		filename: string
		size: number
		fileType: string | null
		width?: number | null
		height?: number | null
		mediaTypeType?: string | null
		duration?: number | null
		thumbnailUrl?: string | null
	} | null>(null)

	// Track if this is the initial mount
	const isInitialMount = useRef(true)
	const isUpdatingFromUrl = useRef(false)

	// Sync URL params to store on mount and when URL changes externally
	useEffect(() => {
		const urlFolder = search.folder || null

		if (isInitialMount.current) {
			// On initial mount, sync URL to store
			isInitialMount.current = false
			if (urlFolder !== selectedFolder) {
				isUpdatingFromUrl.current = true
				setSelectedFolder(urlFolder)
			}
		}
	}, [search.folder, selectedFolder, setSelectedFolder])

	// Sync store changes to URL
	useEffect(() => {
		// Skip if we're updating from URL to avoid loops
		if (isUpdatingFromUrl.current) {
			isUpdatingFromUrl.current = false
			return
		}

		const currentUrlFolder = search.folder || null
		if (selectedFolder !== currentUrlFolder) {
			navigate({
				to: '/home',
				search: selectedFolder ? { folder: selectedFolder } : {},
				replace: true, // Don't add to history for filter changes
			})
		}
	}, [selectedFolder, search.folder, navigate])

	const handleDownload = useCallback((asset: Asset) => {
		if (asset.file) {
			setDownloadFile({
				id: asset.file.id,
				filename: asset.file.filename,
				size: asset.file.size,
				fileType: asset.file.fileType,
				width: asset.file.width,
				height: asset.file.height,
				mediaTypeType: asset.file.mediaTypeType,
				duration: asset.file.duration,
				thumbnailUrl: asset.thumbnailUrl,
			})
			setDownloadModalOpen(true)
		}
	}, [])

	// Right panel for the home view — seamless sections
	const homeRightPanel = useMemo(() => <HomeRightPanel />, [])

	return (
		<div className="h-full">
			<BrowseView
				onDownload={handleDownload}
				showAddToCollection
				homeRightPanel={homeRightPanel}
				className="h-full"
			/>

			{/* Download Modal */}
			<DownloadModal
				open={downloadModalOpen}
				onOpenChange={setDownloadModalOpen}
				file={downloadFile}
			/>
		</div>
	)
}

// ─── Right Panel ────────────────────────────────────────────
// Seamless sections matching the reference — no distinct card borders,
// subtle containers that blend with the page background.

function HomeRightPanel() {
	return (
		<div className="space-y-6">
			{/* Storage / Your Plan */}
			<div className="rounded-xl bg-card border border-border p-5">
				<h3 className="text-sm font-semibold text-foreground mb-3">Your Plan</h3>
				<StatsMeter />
			</div>

			{/* Recent Activities (account-wide feed) */}
			<div className="rounded-xl bg-card border border-border p-5">
				<RecentActivity />
			</div>
		</div>
	)
}
